import { useCallback, useRef, useState } from 'react';

import { KeyCodes } from '../../../utils/keyCodes';

export const useRadio = (props) => {
  const [radioState, setRadioState] = useState();
  const [focused, setFocused] = useState();
  const ref = useRef(null);

  const handleOnChange = useCallback(
    (e, elem, value) => {
      if (!props.disabled && props.onChange) {
        props.onChange({
          originalEvent: e,
          value,
          checked: !elem.checked,
          stopPropagation: () => {},
          preventDefault: () => {},
          target: {
            name: elem.name,
            id: elem.id,
            value,
            checked: !elem.checked,
          },
        });
      }
      if (!elem.checked) {
        elem.checked = !elem.checked;
        setRadioState(value);
      }
      e.preventDefault();
      e.stopPropagation();
    },
    [setRadioState, props],
  );

  const handleKeyDown = useCallback(
    (event, inputRef, value) => {
      switch (event.keyCode) {
        case KeyCodes.space:
          handleOnChange(event, inputRef, value);
          event.preventDefault();
          event.stopPropagation();
          break;
        case KeyCodes.right:
        case KeyCodes.down:
          selectNext(event, inputRef, value);
          event.preventDefault();
          event.stopPropagation();
          break;
        case KeyCodes.left:
        case KeyCodes.up:
          selectPrev(event, inputRef, value);
          event.preventDefault();
          event.stopPropagation();
          break;
        default:
        // do nothing
      }
    },
    [radioState, setRadioState],
  );

  const selectNext = useCallback(
    (event) => {
      if (ref.current) {
        const checkedRadio = ref.current.querySelector(
          '[role=radio][aria-checked=true]',
        );
        const allRadios = Array.from(
          ref.current.querySelectorAll('[role=radio]'),
        );
        if (allRadios.length === 0) {
          return;
        }
        const index = checkedRadio ? allRadios.indexOf(checkedRadio) : 0;
        const nextIndex = (index + 1) % allRadios.length;
        const nextRadio = allRadios[nextIndex];
        const { value } = nextRadio.dataset;
        if (value) {
          nextRadio.focus();
          const inputField = nextRadio.querySelector('[type=radio]');
          handleOnChange(event, inputField, value);
        }
      }
    },
    [ref, handleOnChange],
  );

  const selectPrev = useCallback(
    (event) => {
      if (ref.current) {
        const checkedRadio = ref.current.querySelector(
          '[role=radio][aria-checked=true]',
        );
        const allRadios = Array.from(
          ref.current.querySelectorAll('[role=radio]'),
        );
        if (allRadios.length === 0) {
          return;
        }
        const index = checkedRadio ? allRadios.indexOf(checkedRadio) : 0;
        const prevIndex = (allRadios.length + index - 1) % allRadios.length;
        const prevRadio = allRadios[prevIndex];
        const { value } = prevRadio.dataset;
        if (value) {
          prevRadio.focus();
          const inputField = prevRadio.querySelector('[type=radio]');
          handleOnChange(event, inputField, value);
        }
      }
    },
    [ref, handleOnChange],
  );

  const handleOnFocus = () => {
    setFocused(true);
  };

  const handleOnBlur = () => {
    setFocused(false);
  };

  return {
    handleOnChange,
    handleOnFocus,
    handleOnBlur,
    handleKeyDown,
    selectPrev,
    selectNext,
    radioState,
    setRadioState,
    ref,
    focused,
  };
};
